package com.islamicmedia.shared.domain.model

import com.islamicmedia.shared.util.currentTimeMillis
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

/**
 * Shown when an account has used its fair-use allowance of analyses for the current rolling window
 * (7 days: a weekly limit; it was 24 hours before October 2026).
 *
 * Built from either of two sources, which carry the same facts:
 *  - the server's `usage_cap` (or the older `daily_cap`) SSE error frame on /api/analyze (the
 *    authoritative rejection), or
 *  - the entitlement pre-flight ([Entitlement.dailyLimitNotice]), which stops the user BEFORE the
 *    paid disambiguation search instead of after it.
 *
 * The copy lives here, not in the UIs, so the web and mobile dialogs say exactly the same thing.
 * It is a deliberate "sandwich" (the owner's call): praise, then the limit, then something warm,
 * so a heavy user doesn't feel punished. Users say "searches", so the copy does too. No em dashes
 * anywhere in user-facing copy. The older-app version of this text is server/.../DailyCapMessage.
 */
data class DailyLimitNotice(
    /** The account's limit, or null when the source didn't say. */
    val limit: Int?,
    /** When the next analysis becomes available (epoch ms), or null when unknown. */
    val resetsAtMs: Long?,
    /** Length of the rolling window in days (7 = weekly). Only used for wording. */
    val windowDays: Int = 7,
) {
    val title: String get() = "Allahumma barek!"

    /** The opening praise. The count is only worth celebrating when it's a real number of searches,
     *  so a tiny limit (a test account, a tight override) gets the praise without it. */
    val praise: String get() {
        val blessing = "It's clear you're a dedicated parent! May Allah preserve you and your family. Ameen."
        return if (limit != null && limit >= 3) "$limit searches! WOW! $blessing" else blessing
    }

    /** The limit itself. "We have a weekly limit", never "each account has a limit": it must not
     *  read as though accounts get different limits. */
    val limitText: String get() {
        val limitSentence = when (limit) {
            null -> "Unfortunately, we have a weekly search limit to keep the servers running smoothly for all users, and you've reached it for now."
            1 -> "Unfortunately, we have a weekly limit of 1 search to keep the servers running smoothly for all users, and you've used it for now."
            else -> "Unfortunately, we have a weekly limit of $limit searches to keep the servers running smoothly for all users, and you've used them all for now."
        }
        return "$limitSentence Most parents never reach this limit, you should be proud!"
    }

    val closing: String get() = "You can search again soon. We're truly sorry for the inconvenience, and we'll be right here to help you check what's next."

    /** Milliseconds until the next search frees up (0 once it has), or null when unknown. Always
     *  computed from the absolute [resetsAtMs], so the popup is right whenever it is opened. */
    fun remainingMs(nowMs: Long = currentTimeMillis()): Long? =
        resetsAtMs?.let { (it - nowMs).coerceAtLeast(0) }

    /** True once the wait is over (or was never known), so the dialogs can stop ticking. */
    fun isWaitOver(nowMs: Long = currentTimeMillis()): Boolean = (remainingMs(nowMs) ?: 0L) < 1000

    /**
     * The live countdown for the pill, e.g. "Search again in 2d 04h 15m", "23h 05m 03s", "5m 03s",
     * "42s". Seconds are left out once the wait is a day or more (they would be noise). Minutes and
     * seconds are zero-padded once a bigger unit is shown, so the text doesn't jump about as it
     * ticks. Whole seconds are floored: a full week starts at 6d 23h 59m, never "7d".
     */
    fun countdownText(nowMs: Long = currentTimeMillis()): String {
        val ms = remainingMs(nowMs) ?: return "Please try again a little later"
        val total = ms / 1000
        if (total <= 0L) return "You can search again now"
        val d = total / 86_400
        val h = (total % 86_400) / 3600
        val m = (total % 3600) / 60
        val sec = total % 60
        val left = when {
            d > 0 -> "${d}d ${pad(h)}h ${pad(m)}m"
            h > 0 -> "${h}h ${pad(m)}m ${pad(sec)}s"
            m > 0 -> "${m}m ${pad(sec)}s"
            else -> "${sec}s"
        }
        return "Search again in $left"
    }

    /**
     * The clock time it frees up, e.g. "Tomorrow at 3:43 PM" or "Thursday at 3:43 PM", for someone who'd rather come back than
     * watch a timer. Rounded UP to the minute so it's never early. Null when unknown or already free.
     */
    fun availableAtText(nowMs: Long = currentTimeMillis(), zone: TimeZone = TimeZone.currentSystemDefault()): String? {
        val at = resetsAtMs?.takeIf { it > nowMs } ?: return null
        val rounded = ((at + 59_999) / 60_000) * 60_000
        val time = Instant.fromEpochMilliseconds(rounded).toLocalDateTime(zone)
        val today = Instant.fromEpochMilliseconds(nowMs).toLocalDateTime(zone).date
        val day = when {
            time.date == today -> "Today"
            time.date == today.plus(1, DateTimeUnit.DAY) -> "Tomorrow"
            // Within the next 6 days the weekday name is the clearest ("Thursday"); further out
            // (only if the window is ever lengthened) fall back to the date.
            time.date <= today.plus(6, DateTimeUnit.DAY) ->
                time.date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
            else -> "${time.date}"
        }
        val hour12 = if (time.hour % 12 == 0) 12 else time.hour % 12
        return "$day at $hour12:${pad(time.minute.toLong())} ${if (time.hour < 12) "AM" else "PM"}"
    }

    private fun pad(n: Long) = if (n < 10) "0$n" else "$n"

    companion object {
        /** Path (on the web base URL) of the fair-use section of the Terms of Service. */
        const val FAIR_USE_PATH = "/terms#fair-use"
        const val FAIR_USE_LINK_LABEL = "Fair use policy"
    }
}
